import { useLocation } from 'react-router-dom';

import { useCanSplitRightSlot } from './useRightSlotBreakpoints';

import { useArtifactStore } from '@/stores/useArtifactStore';
import { useProjectHubStore } from '@/stores/useProjectHubStore';
import { type RightPanelType, useRightDockStore } from '@/stores/useRightDockStore';

/**
 * PH-1: derived selector for which surface occupies the right slot in
 * MainWithOptionalCanvas. Mutex precedence: hub > dock > drawer > empty.
 *
 * Mutex enforcement happens at write-time via `_panel-mutex.ts`. This selector
 * is read-only — it just reports the consistent state. The project rail has no
 * compact presentation, so below the split breakpoint it never claims the slot:
 * a hub left open would otherwise hide the compact drawer or dock behind a
 * surface that renders nothing. The precedence order
 * preserves the existing layout rule (`hasGlobalPanel ? <GlobalRightDock /> :
 * <ArtifactDrawer />` at AppLayout.tsx:168) and slots hub on top.
 */
export type VisibleRightPanel =
  | { mode: 'empty' }
  | { mode: 'project-hub' }
  | { mode: 'artifact-drawer' }
  | { mode: 'global-dock'; panel: Exclude<RightPanelType, null> };

export function useVisibleRightPanel(): VisibleRightPanel {
  const location = useLocation();
  const isHubOpen = useProjectHubStore((s) => s.isOpen);
  const canSplit = useCanSplitRightSlot();
  const dockPanel = useRightDockStore((s) => s.activePanel);
  const isDrawerOpen = useArtifactStore((s) => s.isDrawerOpen);
  const planContextId = useArtifactStore((s) => s.planContext?.planId ?? null);
  const planIdFromUrl = new URLSearchParams(location.search).get('planId');
  const activeProjectId = planIdFromUrl || planContextId;

  if (isHubOpen && activeProjectId && canSplit) return { mode: 'project-hub' };
  if (dockPanel) return { mode: 'global-dock', panel: dockPanel };
  if (isDrawerOpen) return { mode: 'artifact-drawer' };
  return { mode: 'empty' };
}
